import React, {
  useEffect,
  useState,
  useContext,
  useMemo,
  useCallback,
} from 'react';
import './style.css';
import ButtonBasic from '../../atoms/buttonBasic/view';

import {
  formatMoney,
  formatTimeStamp,
  formatPercentage,
  formatBoolean,
  dynamicJsonMapper,
  formatPrefix,
  formatUnit,
} from '../../../utils/formating';

import { EmptyArray } from '../../../app/domain/models/empty/empty';
import { lang } from '../../../utils/langs';
import {
  FaChevronDown,
  FaChevronUp,
  FaSort,
  FaSortUp,
  FaSortDown,
} from 'react-icons/fa';
import { devicecontext } from '../../../context/device/context';
import ExcelJS from 'exceljs';
import PaginatedTable from '../../atoms/pagination/pagination';
import TableCellContent from './TableCellContent';
import { AiOutlineDownload, AiOutlineSearch } from 'react-icons/ai';
import TableSkeleton from './TableSkeleton';
import OptionsMenu from '../../atoms/OptionsMenu/view';
import { useTableData } from './useTableData';
import { SORT_DIRECTION, PAGE_SIZE_OPTIONS } from './constants';

const Table = ({
  id,
  data = undefined,
  paginatedData = undefined,
  getFunction = undefined,
  emptyMessage,
  emptyChild,
  onReload,
  maxHeight,
  width,
  children,
  formats = [],
  excludeList = [],
  headersList = [],
  hiddenActions = false,
  mobileCard = undefined,
  onRowClick = undefined,
  allowExport = false,
  exportFileName = 'datos-tabla',
  allowPageSizeControl = false,
  onPageSizeChange = undefined,
  isLoading: externalLoading = undefined,
  showSkeleton = true,
  allowSearch = true,
  onSearch = undefined,
  toolbarActions = undefined,
}) => {
  const [headers, setHeaders] = useState([]);
  const [collapsedCards, setCollapsedCards] = useState(new Map());
  const [pageSize, setPageSize] = useState(paginatedData?.page_size || 10);
  const [searchValue, setSearchValue] = useState('');
  const { isMobileDevice } = useContext(devicecontext);
  const isMobile = isMobileDevice();

  const currentRawData = useMemo(
    () => paginatedData?.data || data || [],
    [paginatedData, data]
  );

  const {
    data: processedData,
    sortConfig,
    handleSort,
    filterQuery,
    setFilterQuery,
  } = useTableData(currentRawData);

  const handleSearchChange = (val) => {
    setSearchValue(val);
    if (onSearch) {
      onSearch(val);
    } else {
      setFilterQuery(val);
    }
  };

  const handleSearchClear = () => {
    setSearchValue('');
    if (onSearch) {
      onSearch('');
    } else {
      setFilterQuery('');
    }
  };

  const handleScroll = useCallback(
    (event) => {
      const { scrollTop, clientHeight, scrollHeight } = event.currentTarget;
      if (scrollHeight - scrollTop === clientHeight) {
        getFunction && getFunction();
      }
    },
    [getFunction]
  );

  const formatFormat = useCallback(
    (param, key) => {
      const formatConfig = formats[key];
      if (!formatConfig || !formatConfig.data || !formatConfig.key)
        return param;
      const foundItem = formatConfig.data.find(
        (item) => item[formatConfig.key] === param
      );
      return foundItem
        ? lang('es', foundItem[formatConfig.displayKey || formatConfig.key])
        : param;
    },
    [formats]
  );

  const formattingOptions = useMemo(
    () => ({
      money: formatMoney,
      unit: formatUnit,
      percentage: formatPercentage,
      timestamp: formatTimeStamp,
      prefix: formatPrefix,
      format: formatFormat,
      boolean: formatBoolean,
      json_path: dynamicJsonMapper,
      tooltip: '',
      input: '',
    }),
    [formatFormat]
  );

  useEffect(() => {
    if (headersList?.length > 0) {
      setHeaders(headersList);
    } else if (currentRawData.length > 0) {
      setHeaders(Object.keys(currentRawData[0]));
    } else {
      setHeaders([]);
    }
  }, [currentRawData, headersList]);

  useEffect(() => {
    if (paginatedData?.page_size) {
      setPageSize(paginatedData.page_size);
    }
  }, [paginatedData?.page_size]);

  const renderChildrenWithProps = (item) =>
    React.Children.map(children, (child) => {
      const randomHex = Math.floor(Math.random() * 16777215).toString(16);
      return React.cloneElement(child, { data: item, id: randomHex });
    });

  const toggleCollapse = (index) => {
    setCollapsedCards((prev) => {
      const newMap = new Map(prev);
      newMap.set(index, !newMap.get(index));
      return newMap;
    });
  };

  const handleExportExcel = async () => {
    if (!processedData || processedData.length === 0) return;

    const realData = processedData.filter(
      (item) => item && item.message !== 'empty_array'
    );
    if (realData.length === 0) return;

    const visibleKeys = Object.keys(realData[0]).filter(
      (key) => !excludeList.includes(key)
    );
    const translatedHeaders = headers
      .filter((h) => !excludeList.includes(h))
      .map((h) => lang('es', h).toUpperCase());

    const workbook = new ExcelJS.Workbook();
    const worksheet = workbook.addWorksheet('Datos');

    const headerRow = worksheet.addRow(translatedHeaders);
    headerRow.eachCell((cell) => {
      cell.fill = {
        type: 'pattern',
        pattern: 'solid',
        fgColor: { argb: 'FF1A73E8' },
      };
      cell.font = { color: { argb: 'FFFFFFFF' }, bold: true };
      cell.alignment = { vertical: 'middle', horizontal: 'center' };
    });

    realData.forEach((item) => {
      const rowValues = visibleKeys.map((key) => {
        let val = item[key];
        if (formats[key]) {
          const formatType = formats[key].type;
          if (
            formattingOptions[formatType] &&
            typeof formattingOptions[formatType] === 'function'
          ) {
            val = formattingOptions[formatType](val, key);
          }
          if (formatType === 'boolean') val = val ? 'Si' : 'No';
        }
        if (val !== null && typeof val === 'object' && !Array.isArray(val))
          val = JSON.stringify(val);
        return val;
      });
      worksheet.addRow(rowValues);
    });

    worksheet.columns.forEach((column) => {
      column.width = 20;
    });

    const buffer = await workbook.xlsx.writeBuffer();
    const blob = new Blob([buffer], {
      type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
    });
    const fileName = `${exportFileName || 'datos'}.xlsx`;

    const link = document.createElement('a');
    link.href = URL.createObjectURL(blob);
    link.download = fileName;
    link.click();
  };

  const handlePageSizeUpdate = (e) => {
    const newSize = parseInt(e.target.value);
    setPageSize(newSize);
    if (onPageSizeChange) onPageSizeChange(newSize);
    if (getFunction) getFunction(1, newSize);
  };

  const visibleDataKeys = useMemo(() => {
    if (!processedData || processedData.length === 0) return [];
    const firstItem = processedData[0];
    if (firstItem.message === 'empty_array' && processedData[1]) {
      return Object.keys(processedData[1]).filter(
        (key) => !excludeList.includes(key)
      );
    }
    return Object.keys(firstItem).filter((key) => !excludeList.includes(key));
  }, [processedData, excludeList]);

  const isLoading =
    externalLoading ?? (data === undefined && paginatedData === undefined);
  const isEmpty =
    currentRawData === EmptyArray ||
    (Array.isArray(currentRawData) &&
      currentRawData.length > 0 &&
      currentRawData[0].message === 'empty_array') ||
    (currentRawData.length === 0 && !isLoading);

  if (isLoading && showSkeleton) return <TableSkeleton />;



  const SortIcon = ({ columnKey }) => {
    // Encontrar cual propiedad del objeto corresponde a este index de header
    const headerIndex = headers.indexOf(columnKey);
    const actualKey = visibleDataKeys[headerIndex] || columnKey;

    if (sortConfig.key !== actualKey) return <FaSort className="sort-icon" />;
    if (sortConfig.direction === SORT_DIRECTION.ASC)
      return <FaSortUp className="sort-icon active" />;
    return <FaSortDown className="sort-icon active" />;
  };

  const startIndex = (paginatedData?.page - 1) * pageSize + 1;
  const endIndex = Math.min(paginatedData?.page * pageSize, paginatedData?.total || 0);

  if (isMobile) {
    if (mobileCard) {
      return (
        <div className="mobile-cards-container" onScroll={handleScroll}>
          {processedData?.map((item, idx) =>
            React.cloneElement(mobileCard, {
              data: item,
              id: idx,
              actions: children,
            })
          )}
        </div>
      );
    }

    return (
      <div className="mobile-cards-container" onScroll={handleScroll}>
        {processedData?.map((item, idx) => {
          const isCardExpanded = !(collapsedCards.get(idx) ?? true);
          const visibleKeys = Object.keys(item).filter(
            (key) => !excludeList.includes(key)
          );
          const firstVisibleKey = visibleKeys[0];

          return (
            <div
              key={idx}
              className={`mobile-card ${isCardExpanded ? 'expanded' : ''}`}
            >
              <div
                className="mobile-card-header"
                onClick={() => toggleCollapse(idx)}
              >
                {firstVisibleKey && (
                  <div className="mobile-card-item-header">
                    <strong>{lang('es', firstVisibleKey)}:</strong>
                    <p>{lang('es', item[firstVisibleKey])}</p>
                  </div>
                )}
                <div className="mobile-card-toggle">
                  <FaChevronDown />
                </div>
              </div>
              {isCardExpanded && (
                <div className="mobile-card-content">
                  {visibleKeys.slice(1).map((key) => (
                    <div key={key} className="mobile-card-item">
                      <strong>{lang('es', key)}:</strong>
                      <TableCellContent
                        item={item}
                        dataKey={key}
                        idx={idx}
                        formats={formats}
                        formattingOptions={formattingOptions}
                        tableId={id}
                      />
                    </div>
                  ))}
                  {children && !hiddenActions && (
                    <div className="mobile-card-actions">
                      {renderChildrenWithProps(item)}
                    </div>
                  )}
                </div>
              )}
            </div>
          );
        })}
      </div>
    );
  }

  const totalColumns =
    (headers?.length || 0) + (children && !hiddenActions ? 1 : 0);

  return (
    <>
      {(allowExport || allowSearch || toolbarActions) && (
        <div className="table-controls">
          <div className="table-controls-left">
            {allowSearch && (
              <div className="table-search-container">
                <AiOutlineSearch className="table-search-icon" />
                <input
                  type="text"
                  placeholder={lang('es', 'buscar') + '...'}
                  value={searchValue}
                  onChange={(e) => handleSearchChange(e.target.value)}
                  className="table-search-input"
                />
                {searchValue && (
                  <button
                    className="table-search-clear"
                    onClick={handleSearchClear}
                    aria-label="Limpiar búsqueda"
                  >
                    &times;
                  </button>
                )}
              </div>
            )}
          </div>
          <div className="table-controls-right">
            {toolbarActions && (
              <div className="table-toolbar-actions">
                {toolbarActions}
              </div>
            )}
            {allowExport && (
              <OptionsMenu>
                <ButtonBasic onClick={handleExportExcel} type="option">
                  <AiOutlineDownload /> {lang('es', 'exportar_excel')}
                </ButtonBasic>
              </OptionsMenu>
            )}
          </div>
        </div>
      )}

      {isEmpty ? (
        <div className="empty-status">
          {emptyChild ? (
            emptyChild
          ) : onReload ? (
            <ButtonBasic showLoading={true} onClick={onReload}>
              Recargar
            </ButtonBasic>
          ) : (
            <p>{emptyMessage || lang('es', 'No hay datos para mostrar')}</p>
          )}
        </div>
      ) : (
        <>
          <div className="table-wrapper" style={{ overflowY: isEmpty ? 'hidden' : 'auto', maxHeight: maxHeight || '60vh' }}>
            <table
              id={id || ''}
              className="table"
              style={{ '--cols': totalColumns }}
            >
              <thead>
                <tr>
                  {headers?.map((headerLabel, idx) => (
                    <th
                      key={idx}
                      scope="col"
                      onClick={() =>
                        handleSort(visibleDataKeys[idx] || headerLabel)
                      }
                    >
                      <div className="header-content">
                        {lang('es', headerLabel).toUpperCase()}
                        <SortIcon columnKey={headerLabel} />
                      </div>
                    </th>
                  ))}
                  {children && !hiddenActions && (
                    <th key="act" scope="col">
                      <div className="header-content">
                        {lang('es', 'acciones').toUpperCase()}
                      </div>
                    </th>
                  )}
                </tr>
              </thead>
              <tbody
                className="table-body"
              >
                {processedData?.map((item, idx) => (
                  <tr
                    key={item.id || item.numero_linea || idx}
                    className="table-row"
                    onClick={() => onRowClick?.(item)}
                    style={{
                      cursor: onRowClick ? 'pointer' : 'default',
                      animationDelay: `${idx * 0.05}s`,
                    }}
                  >
                    {headers.map((_, hIdx) => {
                      const key = visibleDataKeys[hIdx] || (formats && Object.keys(formats)[hIdx]);
                      if (!key) return null;
                      return (
                        <td key={key} scope="row">
                          <TableCellContent
                            item={item}
                            dataKey={key}
                            idx={idx}
                            formats={formats}
                            formattingOptions={formattingOptions}
                            tableId={id}
                          />
                        </td>
                      );
                    })}
                    {children && !hiddenActions && (
                      <td scope="row">
                        <div className="actions-container">
                          {renderChildrenWithProps(item)}
                        </div>
                      </td>
                    )}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {paginatedData?.data && (
            <div className="table-pagination-footer">
              <div className="table-pagination-left">
                {allowPageSizeControl && (
                  <div className="page-size-control">
                    <label>{lang('es', 'mostrar')}:</label>
                    <select value={pageSize} onChange={handlePageSizeUpdate}>
                      {PAGE_SIZE_OPTIONS.map((opt) => (
                        <option key={opt} value={opt}>
                          {opt}
                        </option>
                      ))}
                    </select>
                  </div>
                )}
              </div>

              <div className="table-pagination-right">
                <div className="pagination-range-info">
                  <span>{startIndex}-{endIndex}</span> {lang('es', 'de')} <span>{paginatedData?.total || 0}</span>
                </div>
                <PaginatedTable
                  id={id}
                  itemsCountPerPage={paginatedData?.page_size || 0}
                  totalItemsCount={paginatedData?.total || 0}
                  getFunction={getFunction}
                  currentPage={paginatedData?.page || 1}
                />
              </div>
            </div>
          )}
        </>
      )}
    </>
  );
};

export default Table;
